import { apiDate, number, optional, optionalBoolean, optionalNumber, optionalString, string, zenum, zodLess, ZodLessInfer } from '@kaiizen/zod-less';
import { makeOptionalPartialMetadataSchema, optionalApiDate, optionalMetadataSchema } from '../schemaLib';

export const ApiOrgPropertyFactsSchemaFields = {
  bedroomCount: optionalNumber.nullable(),
  bathroomCount: optionalNumber.nullable(),
  fullBathCount: optionalNumber.nullable(),
  halfBathCount: optionalNumber.nullable(),
  squareFeet: optionalNumber.nullable(),
  vintage: optionalNumber.nullable(),
  acres: optionalNumber.nullable(),
  hasGarage: optionalBoolean.nullable(),
  latitude: optionalNumber.nullable(),
  longitude: optionalNumber.nullable(),
  propertyId: optionalString.nullable(),
  zipCode: optionalString.nullable(),
};
export const ApiOrgPropertyFactsSchema = zodLess(ApiOrgPropertyFactsSchemaFields, { ref: 'orgPropertyFacts' });
export type ApiOrgPropertyFacts = ZodLessInfer<typeof ApiOrgPropertyFactsSchema>;

/**
 * Provenance the server resolves at vivify and returns on `customerMetadata`: read-only. Not part of the
 * vivify or update input, and the server strips them if a client sends them anyway.
 */
export const ApiOrgPropertyProvenanceSchemaFields = {
  abodeMineAupid: optionalString.nullable(),
  taxAssessorId: optionalString.nullable(),
  abodeMineListingPhotoUrl: optionalString.nullable(),
};

/** Client-writable facts only, for the vivify and update input. */
const ApiOrgPropertyInputCustomerMetadataSchema = makeOptionalPartialMetadataSchema(ApiOrgPropertyFactsSchemaFields);

export const ApiOrgPropertySchema = zodLess({
  id: string,
  orgId: string,
  orgMarketId: optionalString.nullable(),
  name: optionalString,
  address: string,
  customerMetadata: makeOptionalPartialMetadataSchema({ ...ApiOrgPropertyFactsSchemaFields, ...ApiOrgPropertyProvenanceSchemaFields }),
  hasMultipleMarkets: optionalBoolean,
  heroScanPhotoId: optionalString.nullable(),
  createdAt: apiDate,
  updatedAt: apiDate,
  archivedAt: optionalApiDate,
}, {
  ref: 'orgProperty',
  description:
    'orgProperty allows orgs to associate their own custom data with properties in Fusion. Exactly one orgProperty record can exist for a given orgId + fusionPropertyId pair.',
});
export const ApiVivifyOrgPropertySchema = ApiOrgPropertySchema
  .omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true, orgId: true, hasMultipleMarkets: true })
  .extend({
    address: string,
    customerMetadata: ApiOrgPropertyInputCustomerMetadataSchema,
    orgMarket: optional({
      name: string,
      zipCodes: zodLess([string]).refine(zipCodes => zipCodes.length > 0, { message: 'At least one zip code is required' }),
      contractorIds: optional([string]), // optional
      users: zodLess([string]).refine(users => users.length > 0, { message: 'At least one user is required' }), // required at least one user
      customerMetadata: optionalMetadataSchema,
    }),
  });
export const ApiUpdateOrgPropertySchema = ApiOrgPropertySchema
  .omit({ id: true, createdAt: true, updatedAt: true, archivedAt: true, orgId: true, hasMultipleMarkets: true })
  .extend({ archivedAt: string.nullable().optional(), customerMetadata: ApiOrgPropertyInputCustomerMetadataSchema }).partial();
export type ApiOrgProperty = ZodLessInfer<typeof ApiOrgPropertySchema>;
export type ApiVivifyOrgProperty = ZodLessInfer<typeof ApiVivifyOrgPropertySchema>;
export type ApiUpdateOrgProperty = ZodLessInfer<typeof ApiUpdateOrgPropertySchema>;

export const getOrgPropertyQueryKeyPrefix = () => `orgProperty`;
export const getOrgPropertyQueryKey = (id?: string | null) => `${getOrgPropertyQueryKeyPrefix()}/${id}`;
export const getOrgPropertiesQueryKeyPrefix = () => `orgProperties`;
export const getOrgPropertiesQueryKey = (archived?: boolean) => `${getOrgPropertiesQueryKeyPrefix()}/${archived ? 'archived' : 'active'}`;

/** The batch property upload CSV's required columns. Header names match without case. */
export const ORG_PROPERTIES_CSV_REQUIRED_COLUMNS = ['orgId', 'address', 'customId', 'market'] as const;
/** The batch property upload CSV's optional columns: home facts stored on the property. */
export const ORG_PROPERTIES_CSV_OPTIONAL_COLUMNS = ['beds', 'baths', 'yearBuilt', 'squareFeet'] as const;
export type OrgPropertiesCsvColumn = (typeof ORG_PROPERTIES_CSV_REQUIRED_COLUMNS)[number] | (typeof ORG_PROPERTIES_CSV_OPTIONAL_COLUMNS)[number];

export const ApiBatchUploadOrgPropertyRowStatusSchema = zenum('ok', 'error');
export const ApiBatchUploadOrgPropertyRowResultSchema = zodLess({
  rowIndex: number,
  orgId: string,
  address: string,
  customId: string,
  market: string,
  status: ApiBatchUploadOrgPropertyRowStatusSchema,
  orgProperty: optional(ApiOrgPropertySchema),
  error: optionalString,
}, { ref: 'batchUploadOrgPropertyRowResult' });

export const ApiBatchUploadOrgPropertiesResponseSchema = zodLess({
  results: zodLess([ApiBatchUploadOrgPropertyRowResultSchema]),
  summary: zodLess({ ok: number, error: number }),
}, { ref: 'batchUploadOrgPropertiesResponse' });
export type ApiBatchUploadOrgPropertiesResponse = ZodLessInfer<typeof ApiBatchUploadOrgPropertiesResponseSchema>;
